/**
 * The repository adapter is the issue-domain seam between the board renderer and the
 * server. It owns the endpoint the board fetches from, the response shape it
 * expects back, and the mapping from that payload into the generic
 * board data the renderer consumes.
 *
 * Today it fetches the Rails Issues list route (`GET /:owner/:repo/issues?q=…&page=1`)
 * and groups the flat list-view response client-side by label. When the board's
 * dedicated REST endpoint lands, swap the fetch and the mapper here — the
 * renderer and the generic board pieces do not need to change.
 *
 * Grouping is pinned to `label` and sort/swimlane are ignored while the mapper is a
 * scaffold. When the real endpoint lands, add the relevant fields to `SavedViewsBoardOptions`
 * and thread them into the request. Do not add them as unused props in the
 * meantime — the type must not lie about what the fetch respects.
 *
 * Paging isn't wired yet (github/views#139); the first page of results is
 * grouped client-side and `hasMore*` axes are always `false`.
 */

import {fromJson} from '@github-ui/dotcom-schema/protobuf'
import {
  type RepoIssuesDashboardContentResponse,
  RepoIssuesDashboardContentResponseSchema,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {queryFnFetchWithProtoSignal} from '@github-ui/react-core/future/query-fn-fetch'

import {axisLabels, type BoardPayloadItem} from '../components/board/issues/board-payload'
import type {IssuesBoardMetadata} from '../components/board/issues/board-metadata'
import {resultsToBoardPayload} from '../components/board/issues/proto-to-board-payload-mapper'
import {toBoardData} from '../components/board/to-board-data'
import type {SavedViewsBoardAdapter} from '../components/board/types'

// The Rails route wraps the payload under its route id; extract it before decoding.
interface DataRouterResponse {
  payload?: Record<string, unknown>
}

const ROUTE_ID = 'issuesIndexContentRoute'

export function createRepositoryBoardAdapter({
  owner,
  repo,
}: {
  owner: string
  repo: string
}): SavedViewsBoardAdapter<BoardPayloadItem, IssuesBoardMetadata> {
  return {
    key: ['repository', owner, repo],
    fetchInitialBoardPayload: async ({query, signal}) => {
      const {json, isProtoJson} = await queryFnFetchWithProtoSignal<DataRouterResponse>({
        routeId: 'issuesIndexContentRoute',
        queryName: 'boardIssues',
        queryDeps: {
          pathname: `/${owner}/${repo}/issues`,
          searchParams: {q: query, page: '1'},
          init: {signal},
        },
      })
      const payload = json.payload?.[ROUTE_ID]
      if (!payload) throw new Error('Unable to find the repository issues payload')
      const data = isProtoJson
        ? fromJson({
            schema: RepoIssuesDashboardContentResponseSchema,
            raw: payload,
            routeId: ROUTE_ID,
            queryName: 'boardIssues',
          })
        : (payload as RepoIssuesDashboardContentResponse)
      const boardPayload = resultsToBoardPayload(data.results)
      return {
        boardData: toBoardData(boardPayload),
        metadata: {
          columnLabels: axisLabels(boardPayload.columns),
          rowLabels: axisLabels(boardPayload.rows),
          pageInfo: data.pageInfo,
          error: data.error,
        },
      }
    },
  }
}
